import React, { useState, useEffect, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { useNavigate } from 'react-router-dom';

import { useStoreValue } from '../../store';
import ApplicationService from '../../service/application-service';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Editor } from 'primereact/editor';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import BlockingOverlay from '../../components/BlockingOverlay';
import getDateFromISOstring from '../../helpers/getDateFromISOstring';

const STATO_VOCE = {
    DA_FARE: { label: 'Da fare', severity: 'warning' },
    IN_ATTESA: { label: 'In attesa', severity: 'info' },
    APPROVATO: { label: 'Approvato', severity: 'success' }
};
const STATO_RICHIESTA = {
    APERTA: { label: 'Aperta', severity: 'warning' },
    IN_ATTESA: { label: 'In attesa', severity: 'info' },
    CHIUSA: { label: 'Chiusa', severity: 'success' }
};

const RichiesteIntegrazioni = () => {
    const navigate = useNavigate();
    const chosenCompanyId = useStoreValue('chosenCompanyId');
    const companies = useStoreValue('companies');
    const company = (companies || []).find(o => o.id === chosenCompanyId);

    const toast = useRef(null);
    const [loading, setLoading] = useState(true);
    const [applicationId, setApplicationId] = useState(null);
    const [richieste, setRichieste] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    const [answersModal, setAnswersModal] = useState(false);
    const [flaggedAnswers, setFlaggedAnswers] = useState([]);
    const [answerValues, setAnswerValues] = useState({});
    // null = tutte le risposte segnalate; altrimenti la sola risposta scelta dalla riga.
    const [modalFieldId, setModalFieldId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const caricaTutto = () => {
        setLoading(true);
        ApplicationService.getApplications((resp) => {
            const apps = (resp && resp.data) ? resp.data : [];
            // Lo storico vive sulla domanda ESG (call 3): in SOCCORSO se c'e' una richiesta aperta,
            // altrimenti l'ultima domanda inviata, cosi' le richieste chiuse restano consultabili.
            const esg = apps.filter(a => !a.callId || Number(a.callId) === 3 || (a.call && Number(a.call.id) === 3));
            const attiva = esg.find(a => a.status === 'SOCCORSO')
                || esg.filter(a => a.status !== 'DRAFT').sort((a, b) => (b.id || 0) - (a.id || 0))[0]
                || apps.find(a => a.status === 'SOCCORSO');
            if (!attiva) { setApplicationId(null); setRichieste([]); setLoading(false); return; }
            setApplicationId(attiva.id);
            ApplicationService.getIntegrationHistory(attiva.id, (r2) => {
                let list = (r2 && Array.isArray(r2.data)) ? r2.data.slice() : [];
                list.sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
                setRichieste(list);
                setLoading(false);
            }, () => setLoading(false));
            ApplicationService.getAnswersReview(attiva.id, (r3) => {
                if (r3 && Array.isArray(r3.data)) {
                    const flagged = r3.data.filter(x => x.daIntegrare);
                    setFlaggedAnswers(flagged);
                    const init = {}; flagged.forEach(f => { init[f.fieldId] = f.value || ''; });
                    setAnswerValues(init);
                }
            }, () => {});
        }, () => setLoading(false));
    };

    useEffect(() => { caricaTutto(); /* eslint-disable-next-line */ }, [chosenCompanyId]);

    const vaiAiDocumenti = () => navigate('/documenti');
    const risposteInModale = () => modalFieldId ? flaggedAnswers.filter(f => f.fieldId === modalFieldId) : flaggedAnswers;
    const apriCorrezione = (fieldId) => { setModalFieldId(fieldId || null); setAnswersModal(true); };
    // Editor della piattaforma per i campi di testo formattato: la risposta si legge e si corregge
    // come nel questionario, non come codice HTML.
    const editorHeader = (
        <span className="ql-formats">
            <button className="ql-bold" aria-label="Bold"></button>
            <button className="ql-italic" aria-label="Italic"></button>
            <button className="ql-underline" aria-label="Underline"></button>
            <button className="ql-list" value="ordered" aria-label="Ordered list"></button>
            <button className="ql-list" value="bullet" aria-label="Bullet list"></button>
        </span>
    );
    const campoRisposta = (r) => r.tipo === 'wysiwyg'
        ? <Editor value={answerValues[r.fieldId] || ''} headerTemplate={editorHeader}
                  onTextChange={(e) => setAnswerValues({ ...answerValues, [r.fieldId]: e.htmlValue || '' })}
                  style={{ height: '180px' }}/>
        : <InputTextarea value={answerValues[r.fieldId] || ''} onChange={(e) => setAnswerValues({ ...answerValues, [r.fieldId]: e.target.value })} rows={4} autoResize style={{ width: '100%' }}/>;

    const inviaIntegrazione = () => {
        if (!applicationId) return;
        setSubmitting(true);
        const answers = risposteInModale().map(f => ({ fieldId: f.fieldId, value: answerValues[f.fieldId] != null ? answerValues[f.fieldId] : '' }));
        ApplicationService.submitIntegrationAnswers(applicationId, { answers }, (resp) => {
            setSubmitting(false);
            if (resp && resp.status === 'SUCCESS') {
                setAnswersModal(false);
                if (toast.current) toast.current.show({ severity: 'success', summary: '', detail: __('Risposte inviate. In attesa di approvazione.', 'htr') });
                caricaTutto();
            } else if (toast.current) {
                toast.current.show({ severity: 'error', summary: '', detail: (resp && resp.message) || __('Invio non riuscito.', 'htr') });
            }
        }, (err) => {
            setSubmitting(false);
            if (toast.current) toast.current.show({ severity: 'error', summary: '', detail: (err && err.message) || __('Invio non riuscito.', 'htr') });
        });
    };

    const selected = richieste.find(r => r.id === selectedId) || null;

    const tagRichiesta = (stato) => {
        const c = STATO_RICHIESTA[stato] || STATO_RICHIESTA.APERTA;
        return <Tag value={__(c.label, 'htr')} severity={c.severity}/>;
    };
    const tagVoce = (stato) => {
        const c = STATO_VOCE[stato] || STATO_VOCE.DA_FARE;
        return <Tag value={__(c.label, 'htr')} severity={c.severity}/>;
    };
    const sottotitoloRichiesta = (req) => {
        if (req.stato === 'CHIUSA') return __('Tutto approvato', 'htr');
        if (req.stato === 'IN_ATTESA') return __('In attesa di approvazione', 'htr');
        return `${req.daFare != null ? req.daFare : req.mancanti}/${req.totale} ${__('da sistemare', 'htr')}`;
    };

    // ---- colonne (impaginazione riusata dalla lista documenti) ----
    const colVoce = (r) => <strong>{r.label}</strong>;
    const colMotivo = (r) => r.note ? r.note : '-';
    const colStato = (r) => tagVoce(r.stato);
    const azioneDocumento = (r) => r.stato === 'DA_FARE'
        ? <Button type="button" size="small" outlined label={__('Aggiorna', 'htr')} icon="pi pi-upload" iconPos="right" onClick={vaiAiDocumenti}/>
        : null;
    const azioneQuestionario = (r) => r.stato === 'DA_FARE'
        ? <Button type="button" size="small" outlined label={__('Correggi', 'htr')} icon="pi pi-pencil" iconPos="right" onClick={() => apriCorrezione(r.fieldId)}/>
        : null;

    return (
        <div className="appPage">
            <div className="appPage__pageHeader">
                <h1>{__('Richieste di integrazioni', 'htr')}</h1>
                {company ? <span className="companyName">{company.companyName}</span> : null}
            </div>

            <Toast ref={toast}/>
            <BlockingOverlay shouldDisplay={loading}/>

            {selected ? (
                /* ---------- DETTAGLIO ---------- */
                <div className="appPage__content">
                    <div className="appPageSection__row" style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap' }}>
                        <Button type="button" outlined size="small" icon="pi pi-arrow-left" iconPos="left"
                                label={__('Torna all’elenco', 'htr')} onClick={() => setSelectedId(null)}/>
                        <Button type="button" size="small" icon="pi pi-folder-open" iconPos="right"
                                label={__('Vai ai documenti', 'htr')} onClick={vaiAiDocumenti}/>
                    </div>

                    <div className="appPageSection__withBorder">
                        <p className="appPageSection__pMeta">
                            <span>{__('Richiesta del', 'htr')}</span>
                            <span>{selected.data ? getDateFromISOstring(new Date(selected.data)) : ''}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Stato', 'htr')}</span>
                            <span>{tagRichiesta(selected.stato)}</span>
                        </p>
                    </div>

                    {(selected.documenti && selected.documenti.length > 0)
                        ? <div className="appPageSection">
                            <h3>{__('Documenti', 'htr')}</h3>
                            <DataTable value={selected.documenti} showGridlines stripedRows dataKey="label">
                                <Column header={__('Documento', 'htr')} body={colVoce} style={{ minWidth: '12rem' }}/>
                                <Column header={__('Motivo', 'htr')} body={colMotivo} style={{ minWidth: '14rem' }}/>
                                <Column header={__('Stato', 'htr')} body={colStato} style={{ width: '130px' }}/>
                                <Column header={__('Azioni', 'htr')} body={azioneDocumento} style={{ width: '150px' }}/>
                            </DataTable>
                        </div> : null}

                    {(selected.questionario && selected.questionario.length > 0)
                        ? <div className="appPageSection">
                            <h3>{__('Questionario', 'htr')}</h3>
                            <DataTable value={selected.questionario} showGridlines stripedRows dataKey="label">
                                <Column header={__('Domanda', 'htr')} body={colVoce} style={{ minWidth: '12rem' }}/>
                                <Column header={__('Motivo', 'htr')} body={colMotivo} style={{ minWidth: '14rem' }}/>
                                <Column header={__('Stato', 'htr')} body={colStato} style={{ width: '130px' }}/>
                                <Column header={__('Azioni', 'htr')} body={azioneQuestionario} style={{ width: '150px' }}/>
                            </DataTable>
                        </div> : null}
                </div>
            ) : (
                /* ---------- ELENCO ---------- */
                <div className="appPage__content">
                    <p>{__('Le richieste di integrazione ricevute, dalla più recente. Apri una richiesta per vedere i documenti e le risposte da sistemare. Le voci che invii restano In attesa finché l’istruttore non le approva; quando è tutto approvato la richiesta risulta Chiusa.', 'htr')}</p>

                    <DataTable value={richieste} showGridlines stripedRows dataKey="id" loading={loading}
                               emptyMessage={__('Non hai richieste di integrazione.', 'htr')}>
                        <Column header={__('Data', 'htr')} style={{ width: '140px' }}
                                body={(r) => r.data ? getDateFromISOstring(new Date(r.data)) : '-'}/>
                        <Column header={__('Da sistemare', 'htr')} body={(r) => sottotitoloRichiesta(r)}/>
                        <Column header={__('Stato', 'htr')} style={{ width: '130px' }} body={(r) => tagRichiesta(r.stato)}/>
                        <Column header={__('Azioni', 'htr')} style={{ width: '120px' }}
                                body={(r) => <Button type="button" size="small" label={__('Apri', 'htr')} icon="pi pi-chevron-right" iconPos="right" onClick={() => setSelectedId(r.id)}/>}/>
                    </DataTable>
                </div>
            )}

            <Dialog
                header={modalFieldId ? __('Correggi la risposta', 'htr') : __('Correggi le risposte del questionario', 'htr')}
                visible={answersModal}
                style={{ width: '46rem', maxWidth: '95vw' }}
                onHide={() => { if (answersModal) setAnswersModal(false); }}>
                <p style={{ marginTop: 0 }}>
                    {__('Correggi le risposte segnalate qui sotto. Al termine premi “Invia integrazione”: resteranno In attesa finché l’istruttore non le approva.', 'htr')}
                </p>
                {risposteInModale().map((r, i) => (
                    <div key={r.fieldId} className="appPageSection__withBorder">
                        <div><strong>{risposteInModale().length > 1 ? `${i + 1}. ` : ''}{r.label}</strong></div>
                        {r.motivo
                            ? <div className="appPageSection__message warning" style={{ margin: '.5rem 0' }}>
                                <span>{__('Motivo:', 'htr')} {r.motivo}</span>
                            </div>
                            : null}
                        {campoRisposta(r)}
                    </div>
                ))}
                <div className="appPageSection__actions" style={{ marginTop: '.5rem' }}>
                    <Button type="button" outlined label={__('Annulla', 'htr')} onClick={() => setAnswersModal(false)}/>
                    <Button type="button" loading={submitting} disabled={submitting} onClick={inviaIntegrazione} label={__('Invia integrazione', 'htr')} icon="pi pi-check" iconPos="right"/>
                </div>
            </Dialog>
        </div>
    );
};

export default RichiesteIntegrazioni;
